import React, {useState, useMemo, useEffect} from 'react'
import styled from 'styled-components'
import { USER_ROLES } from 'utils/userRoles'
import Modal from 'components/atoms/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { cancelInvitationRoutine } from 'features/accessControl/ducks/actions'
import { cancelUnitInvitationRoutine } from 'ducks/units/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { hideEmail, isOwner } from 'utils/user'
import Avatar from 'components/Avatar'
import { accessControlApproveRoutine, accessControlDenyRoutine } from 'features/bpDetails/ducks/actions'
import SearchIcon from "@mui/icons-material/Search"
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined'
import { formatDate } from 'utils/date'
import { isEmpty } from 'ramda'
import SearchableSelect from 'components/SearchableSelect'
import Tooltip from '@mui/material/Tooltip'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { RAG } from 'features/dashboard/charts/scoreColour'

const BREAKPOINT_MOBILE = 768

const STATUS_ORDER = ['PENDING', 'NOT_JOINED', 'APPROVED', 'DENIED']

const STATUS_META = {
  APPROVED: { label: 'Accepted', tip: 'This person accepted the invitation and can now see this passport.', rag: RAG.low, Icon: CheckCircleIcon },
  NOT_JOINED: { label: 'Invited, not joined yet', tip: 'The invitation was sent, but this person has not created their account yet. They will appear in People once they sign up.', rag: RAG.medium, Icon: MarkEmailUnreadOutlinedIcon },
  PENDING: { label: 'Waiting for approval', tip: 'This invitation is waiting for someone to approve or accept it.', rag: RAG.medium, Icon: HourglassEmptyIcon },
  DENIED: { label: 'Declined', tip: 'This invitation was turned down, so it gives no access.', rag: RAG.critical, Icon: CancelIcon }
}

const PLACEHOLDER_NAMES = new Set(['', 'n/a', 'na', 'null', 'undefined', '-'])

export const cleanNamePart = part => {
  const value = String(part ?? '').trim()
  return PLACEHOLDER_NAMES.has(value.toLowerCase()) ? undefined : value
}

export const invitationDisplayName = invitation => {
  const parts = [invitation?.firstName, invitation?.lastName]
    .map(part => String(part ?? '').trim())
    .filter(part => !PLACEHOLDER_NAMES.has(part.toLowerCase()))
  if (parts.length) return parts.join(' ')
  return invitation?.email ? hideEmail(invitation.email) : 'Name not given'
}

export const normaliseInvitationStatus = raw => {
  const value = String(raw || 'PENDING').toUpperCase()
  if (['APPROVED', 'ACCEPTED'].includes(value)) return 'APPROVED'
  if (['DENIED', 'DECLINED', 'REJECTED'].includes(value)) return 'DENIED'
  return 'PENDING'
}

export const invitationStatusOf = invitation => {
  const status = normaliseInvitationStatus(invitation?.status || invitation?.invitationStatus || invitation?.state)
  const tracksJoining = invitation && ('userId' in invitation || 'user_id' in invitation)
  if (status === 'APPROVED' && tracksJoining && !(invitation.userId || invitation.user_id)) return 'NOT_JOINED'
  return status
}

export const InvitationStatus = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META[normaliseInvitationStatus(status)]
  const { Icon } = meta
  return (
    <Tooltip title={meta.tip}>
      <StatusPill tabIndex={0} $fg={meta.rag.fg} $bg={meta.rag.bg} aria-label={`${meta.label}. ${meta.tip}`}>
        <Icon style={{ fontSize: 15 }} aria-hidden='true' />
        <span>{meta.label}</span>
      </StatusPill>
    </Tooltip>
  )
}

const normalizeRelation = (rt) => {
  if (!rt) return null
  if (typeof rt === 'string') return rt
  if (typeof rt === 'object') {
    return rt.value || rt.code || rt.key || rt.type || rt.name || rt.relation || null
  }
  return null
}

const useInvitationRow = (invitation, unit, canAccept, onCancel, onChanged, scopeType, scopeId) => {
  const [cancelOpen, setCancelOpen] = useState(false)
  const [denyOpen, setDenyOpen] = useState(false)
  const dispatch = useDispatch()
  const currentBp = useSelector(getSelectedBp)
  const currentUser = useSelector(state => state.auth.user)

  const openCancel = () => setCancelOpen(true)
  const closeCancel = () => setCancelOpen(false)
  const openDeny = () => setDenyOpen(true)
  const closeDeny = () => setDenyOpen(false)

  const displayName = useMemo(() => invitationDisplayName(invitation), [invitation])

  const rawRelation = invitation.relationType ?? invitation.relation_type ?? invitation.role
  const roleKey = normalizeRelation(rawRelation)
  const roleLabel =
    (roleKey && USER_ROLES[roleKey]) ||
    (typeof rawRelation === 'object' && (rawRelation.label || rawRelation.name)) ||
    (roleKey || 'Not set')

  const status = invitationStatusOf(invitation)
  const isPending = status === 'PENDING'
  const expiresAt = invitation.expiresAt || invitation.expires_at || null

  const onCancelInvitation = () => {
    if (onCancel) {
      onCancel(invitation)
    } else if (unit) {
      dispatch(cancelUnitInvitationRoutine({
        bpId: currentBp.id,
        unitId: unit.id,
        invitationId: invitation.id
      }))
    } else {
      dispatch(cancelInvitationRoutine({
        bpId: invitation.buildingPassportId || currentBp.id,
        invitationId: invitation.id
      }))
    }
    closeCancel()
  }

  const isContainerScope = !!scopeType && scopeType !== 'building'
  const passportId = isContainerScope ? null : (invitation.buildingPassportId || scopeId || currentBp?.id || null)
  const ownsPassport = !!passportId && !!currentBp && String(currentBp.id) === String(passportId) && isOwner(currentUser, currentBp)
  const sentByMe = !!currentUser?.id && String(invitation.inviterId ?? '') === String(currentUser.id)
  const canApprove = ownsPassport && canAccept && isPending && !sentByMe
  const canWithdraw = isPending && !canApprove && (sentByMe || !!canAccept)

  const onApprove = () => {
    if (!passportId) return
    dispatch(accessControlApproveRoutine.trigger({
      bpId: passportId,
      invitationId: invitation.id,
      onSuccess: onChanged,
    }))
  }

  const onDeny = () => {
    closeDeny()
    if (!passportId) return
    dispatch(accessControlDenyRoutine.trigger({
      bpId: passportId,
      invitationId: invitation.id,
      onSuccess: onChanged,
    }))
  }

  return {
    displayName,
    roleLabel,
    status,
    isPending,
    expiresAt,
    cancelOpen,
    denyOpen,
    openCancel,
    closeCancel,
    openDeny,
    closeDeny,
    onCancelInvitation,
    onApprove,
    onDeny,
    currentUser,
    currentBp,
    canAccept,
    canApprove,
    canWithdraw
  }
}

const InvitationRowMobile = ({ invitation, unit, canAccept, onCancel, onChanged, scopeType, scopeId }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    displayName,
    roleLabel,
    status,
    expiresAt,
    cancelOpen,
    denyOpen,
    openCancel,
    closeCancel,
    openDeny,
    closeDeny,
    onCancelInvitation,
    onApprove,
    onDeny,
    canApprove,
    canWithdraw
  } = useInvitationRow(invitation, unit, canAccept, onCancel, onChanged, scopeType, scopeId)

  const toggleExpand = (e) => {
    e?.stopPropagation?.()
    setIsExpanded(!isExpanded)
  }

  return (
    <>
      <MobileCard onClick={toggleExpand}>
        <MobileCardContent>
          <Avatar firstName={cleanNamePart(invitation.firstName)} lastName={cleanNamePart(invitation.lastName)} email={invitation.email} src={invitation.avatar} size={30} />
          <CardContent>
            <CardTop>
              <NameLabel>{displayName}</NameLabel>
            </CardTop>
            <EmailLabel>{invitation.email ? hideEmail(invitation.email) : 'Not set'}</EmailLabel>
            {isExpanded && (
              <>
                <CardDetails>
                  <DetailRow>
                    <DetailLabel>Status:</DetailLabel>
                    <DetailValue>
                      <InvitationStatus status={status} />
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Role:</DetailLabel>
                    <DetailValue>{roleLabel}</DetailValue>
                  </DetailRow>
                  {expiresAt && (
                    <DetailRow>
                      <DetailLabel>Expires:</DetailLabel>
                      <DetailValue>{formatDate(expiresAt)}</DetailValue>
                    </DetailRow>
                  )}
                </CardDetails>
                {canApprove || canWithdraw ? (
                  <ActionButtons onClick={(e) => e.stopPropagation()}>
                    {canApprove && (
                      <>
                        <ActionButton onClick={(e) => { e.stopPropagation(); onApprove(); }}>Accept</ActionButton>
                        <ActionButton $red onClick={(e) => { e.stopPropagation(); openDeny(); }}>Deny</ActionButton>
                      </>
                    )}
                    {canWithdraw && (
                      <ActionButton $red aria-label={`Withdraw the invitation for ${displayName}`} title='Withdraw this invitation' onClick={(e) => { e.stopPropagation(); openCancel(); }}>Withdraw</ActionButton>
                    )}
                  </ActionButtons>
                ) : null}
              </>
            )}
          </CardContent>
          <ChevronIcon $isExpanded={isExpanded}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </ChevronIcon>
        </MobileCardContent>
      </MobileCard>

      <Modal
        title='Deny invitation'
        open={denyOpen}
        onClose={closeDeny}
        onCancel={closeDeny}
        onSubmit={onDeny}
        withCancel
        withSubmit
      >
        Do you really want to deny this invitation?
      </Modal>

      <Modal
        title='Cancel invitation'
        open={cancelOpen}
        onClose={closeCancel}
        onCancel={closeCancel}
        onSubmit={onCancelInvitation}
        submitButtonText='Withdraw invitation'
        withCancel
        withSubmit
      >
        Do you really want to cancel this invitation?
      </Modal>
    </>
  )
}

const InvitationRowDesktop = ({ invitation, unit, canAccept, onCancel, onChanged, scopeType, scopeId }) => {
  const {
    displayName,
    roleLabel,
    status,
    expiresAt,
    cancelOpen,
    denyOpen,
    openCancel,
    closeCancel,
    openDeny,
    closeDeny,
    onCancelInvitation,
    onApprove,
    onDeny,
    canApprove,
    canWithdraw
  } = useInvitationRow(invitation, unit, canAccept, onCancel, onChanged, scopeType, scopeId)

  return (
    <>
      <DesktopTableRow>
        <TableCell>
          <TableRowContent>
            <Avatar firstName={cleanNamePart(invitation.firstName)} lastName={cleanNamePart(invitation.lastName)} email={invitation.email} src={invitation.avatar} size={30} />
            <NameLabel>{displayName}</NameLabel>
          </TableRowContent>
        </TableCell>
        <TableCell>{invitation.email ? hideEmail(invitation.email) : 'Not set'}</TableCell>
        <TableCell>{roleLabel}</TableCell>
        <TableCell>{expiresAt ? formatDate(expiresAt) : 'Not set'}</TableCell>
        <TableCell>
          <InvitationStatus status={status} />
        </TableCell>
        <TableCell>
          {canApprove || canWithdraw ? (
            <ActionButtonsInline>
              {canApprove && (
                <>
                  <ActionButtonSmall onClick={onApprove}>Accept</ActionButtonSmall>
                  <ActionButtonSmall $red onClick={openDeny}>Deny</ActionButtonSmall>
                </>
              )}
              {canWithdraw && (
                <ActionButtonSmall $red aria-label={`Withdraw the invitation for ${displayName}`} title='Withdraw this invitation' onClick={openCancel}>Withdraw</ActionButtonSmall>
              )}
            </ActionButtonsInline>
          ) : <NoActions>No actions</NoActions>}
        </TableCell>
      </DesktopTableRow>

      <Modal
        title='Deny invitation'
        open={denyOpen}
        onClose={closeDeny}
        onCancel={closeDeny}
        onSubmit={onDeny}
        withCancel
        withSubmit
      >
        Do you really want to deny this invitation?
      </Modal>

      <Modal
        title='Cancel invitation'
        open={cancelOpen}
        onClose={closeCancel}
        onCancel={closeCancel}
        onSubmit={onCancelInvitation}
        submitButtonText='Withdraw invitation'
        withCancel
        withSubmit
      >
        Do you really want to cancel this invitation?
      </Modal>
    </>
  )
}

const SORTERS = {
  name: invitation => [invitation.firstName, invitation.lastName].filter(Boolean).join(' ').toLowerCase(),
  email: invitation => (invitation.email || '').toLowerCase(),
  role: invitation => String(invitation.relationType ?? invitation.relation_type ?? invitation.role ?? '').toLowerCase(),
  expires: invitation => {
    const t = Date.parse(invitation.expiresAt || invitation.expires_at || '')
    return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t
  },
  status: invitation => STATUS_ORDER.indexOf(invitationStatusOf(invitation))
}

const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'expires', label: 'Expires' },
  { key: 'status', label: 'Status' }
]

export const INVITATIONS_PAGE = 25

export const MOBILE_SORT_OPTIONS = [
  { value: 'name:asc', label: 'Sort by name, A to Z' },
  { value: 'name:desc', label: 'Sort by name, Z to A' },
  { value: 'expires:asc', label: 'Sort by expiry, soonest first' },
  { value: 'expires:desc', label: 'Sort by expiry, latest first' },
  { value: 'status:asc', label: 'Sort by status' },
  { value: 'role:asc', label: 'Sort by role' }
]

export const filterAndSortInvitations = (invitations, { search = '', status = null, sort = { key: 'name', dir: 'asc' } } = {}) => {
  const q = search.trim().toLowerCase()
  const matched = invitations.filter(invitation => {
    const current = invitationStatusOf(invitation)
    if (status && current !== status) return false
    if (!q) return true
    const name = [invitation.firstName, invitation.lastName].filter(Boolean).join(' ').toLowerCase()
    const email = (invitation.email || '').toLowerCase()
    const role = String(invitation.relationType ?? invitation.relation_type ?? invitation.role ?? '').toLowerCase()
    const roleName = String(USER_ROLES[role] || '').toLowerCase()
    return name.includes(q) || email.includes(q) || role.includes(q) || roleName.includes(q)
  })
  const pick = SORTERS[sort.key] || SORTERS.name
  const dir = sort.dir === 'desc' ? -1 : 1
  return [...matched].sort((a, b) => {
    const av = pick(a)
    const bv = pick(b)
    if (av < bv) return -1 * dir
    if (av > bv) return 1 * dir
    return 0
  })
}

const InvitationsSection = ({ invitations = [], unit, canAccept, onCancel, onChanged, onCountChange = () => {}, scopeType, scopeId }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState(null)
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' })
  const [visible, setVisible] = useState(INVITATIONS_PAGE)

  const statusCounts = useMemo(() => invitations.reduce((acc, invitation) => {
    const key = invitationStatusOf(invitation)
    return { ...acc, [key]: (acc[key] || 0) + 1 }
  }, {}), [invitations])

  const filteredInvitations = useMemo(
    () => filterAndSortInvitations(invitations, { search: searchQuery, status: statusFilter, sort }),
    [invitations, searchQuery, statusFilter, sort]
  )

  useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(filteredInvitations.length)
    }
  }, [filteredInvitations, onCountChange])

  useEffect(() => {
    setVisible(INVITATIONS_PAGE)
  }, [searchQuery, statusFilter, sort])

  const shownInvitations = filteredInvitations.slice(0, visible)
  const hasMore = visible < filteredInvitations.length

  const toggleSort = key => setSort(prev => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  return (
    <Container>
      <SectionHeaderRow>
        <SectionTitle>Invitations ({invitations.length.toLocaleString('en-GB')})</SectionTitle>
      </SectionHeaderRow>

      <SearchWrapper>
        <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
        <SearchInput
          placeholder='Search by name, email or role'
          aria-label='Search invitations by name, email or role'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchWrapper>

      <FilterChips role='group' aria-label='Show invitations by status'>
        <FilterChip type='button' aria-pressed={!statusFilter} $on={!statusFilter} onClick={() => setStatusFilter(null)}>
          All ({invitations.length.toLocaleString('en-GB')})
        </FilterChip>
        {STATUS_ORDER.map(key => (
          <FilterChip key={key} type='button' aria-pressed={statusFilter === key} $on={statusFilter === key} onClick={() => setStatusFilter(statusFilter === key ? null : key)}>
            {STATUS_META[key].label} ({(statusCounts[key] || 0).toLocaleString('en-GB')})
          </FilterChip>
        ))}
      </FilterChips>

      <MobileSortRow>
        <SearchableSelect
          ariaLabel='Sort invitations'
          searchable={false}
          value={`${sort.key}:${sort.dir}`}
          onChange={v => { const [key, dir] = String(v).split(':'); setSort({ key, dir }) }}
          options={MOBILE_SORT_OPTIONS}
        />
      </MobileSortRow>

      <MobileListView>
        {!isEmpty(shownInvitations) ? (
          shownInvitations.map((invitation) => (
            <InvitationRowMobile
              key={invitation.id}
              invitation={invitation}
              unit={unit}
              canAccept={canAccept}
              onCancel={onCancel}
              onChanged={onChanged}
              scopeType={scopeType}
              scopeId={scopeId}
            />
          ))
        ) : (
          <EmptyStateText>{searchQuery || statusFilter ? 'No invitations match your search' : 'No invitations yet'}</EmptyStateText>
        )}
      </MobileListView>

      <DesktopTableView>
        <StyledTable>
          <thead>
            <tr>
              {COLUMNS.map(column => {
                const active = sort.key === column.key
                return (
                  <th key={column.key} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <SortButton type='button' onClick={() => toggleSort(column.key)} $active={active}>
                      {column.label}
                      <SortMark aria-hidden='true'>{active ? (sort.dir === 'asc' ? '▲' : '▼') : ''}</SortMark>
                    </SortButton>
                  </th>
                )
              })}
              <th style={{ width: 170 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isEmpty(shownInvitations) ? (
              shownInvitations.map((invitation) => (
                <InvitationRowDesktop
                  key={invitation.id}
                  invitation={invitation}
                  unit={unit}
                  canAccept={canAccept}
                  onCancel={onCancel}
                  onChanged={onChanged}
                  scopeType={scopeType}
                  scopeId={scopeId}
                />
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>
                  {searchQuery || statusFilter ? 'No invitations match your search' : 'No invitations yet'}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </DesktopTableView>

      {filteredInvitations.length > 0 && (
        <ListFooter>
          <PaginationInfo>
            Showing {shownInvitations.length.toLocaleString('en-GB')} of {filteredInvitations.length.toLocaleString('en-GB')} invitations
          </PaginationInfo>
          <InfiniteScrollSentinel hasMore={hasMore} loading={false} onLoadMore={() => setVisible(v => v + INVITATIONS_PAGE)} />
        </ListFooter>
      )}
    </Container>
  )
}

export default InvitationsSection

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $fg }) => $fg};
  background: ${({ $bg }) => $bg};
  cursor: help;

  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const NoActions = styled.span`
  font-size: 14px;
  color: #64748b;
`

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
  }
`

const FilterChip = styled.button`
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? '#33becc' : '#cbd5e1')};
  background: ${({ $on }) => ($on ? '#e6f7f9' : '#ffffff')};
  color: #0c1229;
  font-size: 14px;
  font-weight: ${({ $on }) => ($on ? 700 : 500)};
  cursor: pointer;
`

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  color: inherit;
  cursor: pointer;
`

const SortMark = styled.span`
  font-size: 11px;
  min-width: 10px;
`

const ListFooter = styled.div`
  padding: 12px 0;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 12px 16px;
  }
`

const Container = styled.div`
  width: 100%;
  padding: 0 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0;
  }
`

const SectionHeaderRow = styled.div`
  margin-bottom: 12px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 16px;
  }
`

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  gap: 10px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:focus-within {
    background: white;
    border-color: #94a3b8;
    box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.1);
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    margin: 0 16px 20px 16px;
  }
`

const SearchInput = styled.input`
  border: none;
  background: transparent;
  width: 100%;
  font-size: 15px;
  color: #1e293b;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`

const MobileSortRow = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: block;
    padding: 0 16px 12px;
  }
`

const MobileListView = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: flex;
    flex-direction: column;
  }
`

const MobileCard = styled.div`
  background: white;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px;
  cursor: pointer;
  transition: background 0.1s;

  &:active {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`

const MobileCardContent = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  color: #94a3b8;
  transition: transform 0.2s;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const NameLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`

const EmailLabel = styled.span`
  font-size: 13px;
  color: #94a3b8;
`

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`

const DetailLabel = styled.span`
  color: #64748b;
  font-weight: 500;
`

const DetailValue = styled.span`
  color: #1e293b;
`


const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }
`

/* DESKTOP TABLE VIEW */
const DesktopTableView = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    display: none;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    letter-spacing: 0.5px;
  }

  tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.1s;

    &:hover {
      background: #f8fafc;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`

const DesktopTableRow = styled.tr``

const TableCell = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: #1e293b;
`

const TableRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ActionButtonsInline = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const ActionButtonSmall = styled.button`
  min-height: 40px;
  padding: 8px 14px;
  border: 1px solid ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  background: ${({ $red }) => ($red ? '#d14724' : '#10b981')};
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`

const EmptyStateText = styled.div`
  padding: 30px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`



const PaginationInfo = styled.div`
  color: #475569;
  font-size: 14px;
`








